import React, { useRef, useState, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { Document } from '@/hooks/useDocuments';
import { getDocumentIcon } from '@/lib/document-icons';
import { useMatterLocale } from '@/components/contexts/MatterJurisdictionContext';
import { formatRegionalDate } from '@/utils/date-utils';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import { DuplicateLocationsIndicator } from '@/components/shared/DuplicateLocationsIndicator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { getSubtleTagStyle } from '@/components/ui/column-builders';

type TableType = 'correspondence' | 'case-files' | 'all-files';

interface AttachmentRowProps {
  attachment: Document;  // Attachments are Documents
  slug: string;
  parentDocumentGuid: string;
  onDocumentClick: (documentGuid: string) => void;
  isLast?: boolean;
  /** Table type determines column layout - correspondence has 10 cols, case-files has 7, all-files has 8 */
  tableType?: TableType;
}

// Renders as a table row (<tr>) to align with parent table columns
// Must be used inside the same table as parent rows
export const AttachmentRow: React.FC<AttachmentRowProps> = ({
  attachment,
  slug,
  parentDocumentGuid,
  onDocumentClick,
  isLast = false,
  tableType = 'correspondence',
}) => {
  const { activeDocumentGuid, openDocumentPanel } = useSidePanel();
  const locale = useMatterLocale();
  const isHighlighted = activeDocumentGuid === attachment.id;

  const openAttachment = () => {
    openDocumentPanel({
      documentGuid: parentDocumentGuid,
      activeTab: 'document',
      attachmentGuid: attachment.id,
    });
  };

  // Strip extension from display name, matching standalone row behavior
  const displayName = (attachment.displayName || attachment.fileName).replace(/\.[^/.]+$/, "");

  // Check if this attachment has nested attachments (for email-with-attachments icon)
  const hasNestedAttachments = Array.isArray(attachment.attachments) && attachment.attachments.length > 0;
  const { Icon, colorClass } = getDocumentIcon(attachment.fileName, hasNestedAttachments);
  const duplicateLocations = attachment.duplicateLocations ?? [];

  // Track if summary is truncated
  const summaryRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = summaryRef.current;
    if (!el) return;

    const checkTruncation = () => {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    };

    checkTruncation();

    const resizeObserver = new ResizeObserver(checkTruncation);
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, [attachment.summary]);

  // File icon cell - clickable to open document panel, matches parent style (h-5 w-5).
  // Left-aligned like the parent file-type cell so the marks share one column edge.
  const fileIconCell = (
    <div className="flex justify-start">
      <div className="relative flex items-center">
        <button
          onClick={(e) => {
            e.stopPropagation();
            openAttachment();
          }}
          className="cursor-pointer hover:opacity-70 transition-opacity"
        >
          <Icon className={`h-5 w-5 ${colorClass}`} />
        </button>
        <DuplicateLocationsIndicator
          slug={slug}
          locations={duplicateLocations}
          onDocumentClick={onDocumentClick}
        />
      </div>
    </div>
  );

  // Date cell - matches parent style
  const dateCell = attachment.date && (
    <div className="whitespace-nowrap min-w-[90px]">
      {formatRegionalDate(attachment.date, locale)}
    </div>
  );

  // Filename cell - matches parent style (font-medium hover:underline)
  const filenameCell = (
    <button
      onClick={(e) => {
        e.stopPropagation();
        openAttachment();
      }}
      className="font-medium hover:underline cursor-pointer text-left truncate block max-w-full"
      title={displayName}
    >
      {displayName}
    </button>
  );

  // Summary cell - matches parent style, tooltip shows full text when truncated
  const summaryCell = attachment.summary && (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span ref={summaryRef} className="truncate block">
            {attachment.summary}
          </span>
        </TooltipTrigger>
        {isTruncated && (
          <TooltipContent className="max-w-sm">
            <p className="text-sm">{attachment.summary}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );

  // Document type / folder cell for all-files - matches parent style
  const folderCell = attachment.document_type?.name && (
    <div className="flex items-center">
      {attachment.document_type.guid ? (
        <Link
          to="/matter/$slug/case-files/folder/$folderGuid"
          params={{ slug, folderGuid: attachment.document_type.guid }}
          className="hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {attachment.document_type.name}
        </Link>
      ) : (
        <span>{attachment.document_type.name}</span>
      )}
    </div>
  );

  // From cell - for email attachments, matches parent style
  const fromCell = attachment.from && attachment.from.length > 0 && (
    <div className="wrap-break-word">
      {attachment.from.join(", ")}
    </div>
  );

  // To cell - for email attachments, matches parent style
  const toCell = attachment.to && attachment.to.length > 0 && (
    <div className="wrap-break-word">
      {attachment.to.join(", ")}
    </div>
  );

  // Tags cell for correspondence - matches parent style
  const tagsCell = attachment.tags && attachment.tags.length > 0 && (
    <div className="flex flex-wrap gap-1">
      {attachment.tags.map((tag, index) => {
        const baseColor = attachment.tag_colors?.[tag] || '#CCCCCC';

        return (
          <span
            key={index}
            className="px-2 py-0.5 rounded-sm text-xs font-medium"
            style={getSubtleTagStyle(baseColor)}
          >
            {tag}
          </span>
        );
      })}
    </div>
  );

  const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
    // Ignore clicks on inline controls (icon/filename links already open the attachment).
    if (
      (event.target as Element | null)?.closest?.(
        'button, a, input, textarea, select, label, [role="menuitem"], [role="menuitemcheckbox"], [role="checkbox"], [role="option"], [role="listbox"], [role="dialog"], [role="alertdialog"], [contenteditable="true"]',
      )
    ) {
      return;
    }
    openAttachment();
  };

  // Case files layout: Checkbox | FileType | Date | FileName | Summary | Actions (6 columns)
  if (tableType === 'case-files') {
    return (
      <tr onClick={handleRowClick} className={`${isHighlighted ? 'bg-blue-50 dark:bg-blue-900/30' : 'bg-slate-50/50 hover:bg-slate-100/50 cursor-pointer'} transition-colors ${!isLast ? 'border-b border-slate-100' : ''}`}>
        {/* Checkbox column - empty */}
        <td className="px-4 py-2" />

        {/* File Type Icon — same horizontal padding as the parent file-type column */}
        <td className="px-6 py-2">
          {fileIconCell}
        </td>

        {/* Date column */}
        <td className="px-4 py-2">
          {dateCell}
        </td>

        {/* FileName column */}
        <td className="px-4 py-2 overflow-hidden max-w-0">
          {filenameCell}
        </td>

        {/* Summary column */}
        <td className="px-4 py-2 overflow-hidden max-w-0">
          {summaryCell}
        </td>

        {/* Actions column - empty */}
        <td className="px-4 py-2" />
      </tr>
    );
  }

  // All-files layout: Checkbox | FileType | Date | FileName | DocumentType | Summary | Actions (7 columns)
  if (tableType === 'all-files') {
    return (
      <tr onClick={handleRowClick} className={`${isHighlighted ? 'bg-blue-50 dark:bg-blue-900/30' : 'bg-slate-50/50 hover:bg-slate-100/50 cursor-pointer'} transition-colors ${!isLast ? 'border-b border-slate-100' : ''}`}>
        {/* Checkbox column - empty */}
        <td className="px-4 py-2" />

        {/* File Type Icon — same horizontal padding as the parent file-type column */}
        <td className="px-6 py-2">
          {fileIconCell}
        </td>

        {/* Date column */}
        <td className="px-4 py-2">
          {dateCell}
        </td>

        {/* FileName column */}
        <td className="px-4 py-2 overflow-hidden max-w-0">
          {filenameCell}
        </td>

        {/* DocumentType / Folder column */}
        <td className="px-4 py-2">
          {folderCell}
        </td>

        {/* Summary column */}
        <td className="px-4 py-2 overflow-hidden max-w-0">
          {summaryCell}
        </td>

        {/* Actions column - empty */}
        <td className="px-4 py-2" />
      </tr>
    );
  }

  // Correspondence layout: Checkbox | FileType | Date | From | To | Summary | Tags | Actions (8 columns)
  return (
    <tr onClick={handleRowClick} className={`${isHighlighted ? 'bg-blue-50 dark:bg-blue-900/30' : 'bg-slate-50/50 hover:bg-slate-100/50 cursor-pointer'} transition-colors ${!isLast ? 'border-b border-slate-100' : ''}`}>
      {/* Checkbox column - empty */}
      <td className="px-4 py-2" />

      {/* File Type Icon — same horizontal padding as the parent file-type column */}
      <td className="px-6 py-2">
        {fileIconCell}
      </td>

      {/* Date column */}
      <td className="px-4 py-2">
        {dateCell}
      </td>

      {/* From column */}
      <td className="px-4 py-2">
        {fromCell}
      </td>

      {/* To column */}
      <td className="px-4 py-2">
        {toCell}
      </td>

      {/* Summary column - show filename and summary, spans to end of row (Summary + Tags + Actions) */}
      <td className="px-4 py-2 overflow-hidden max-w-0" colSpan={3}>
        <div className="flex flex-col gap-0.5 min-w-0 overflow-hidden">
          {/* Filename styled to match parent email subject: text-gray-500 text-xs */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              openAttachment();
            }}
            className="text-gray-500 text-xs hover:underline cursor-pointer text-left truncate block max-w-full"
            title={displayName}
          >
            {displayName}
          </button>
          {summaryCell}
        </div>
      </td>
    </tr>
  );
};
